/**
 * The custom app setup wizard's steps and gates, pure so they can be pinned without a browser.
 * The link rules mirror `src/utils/shopifyInstallLink.ts`, so the wizard never says "Looks right."
 * about a link the install would then refuse. The server's check still decides.
 */
export const CUSTOM_APP_STEP_IDS = [
  'open',
  'create',
  'urls',
  'install',
  'scopes',
  'release',
  'distribute',
  'credentials',
] as const;

export type CustomAppStepId = (typeof CUSTOM_APP_STEP_IDS)[number];

export const CUSTOM_APP_STEP_COUNT = CUSTOM_APP_STEP_IDS.length;

/**
 * Screenshots of the Shopify Dev Dashboard, by step, served from `frontend/public`. A step with no
 * entry shows an empty slot. See `docs/internal/SHOPIFY_CUSTOM_APP_WIZARD.md` for how to add them.
 */
export const CUSTOM_APP_SCREENSHOTS: Partial<Record<CustomAppStepId, string>> = {
  open: '/shopify-custom-app/step-1.png',
  create: '/shopify-custom-app/step-2.png',
  urls: '/shopify-custom-app/step-3.png',
  install: '/shopify-custom-app/step-4.png',
  scopes: '/shopify-custom-app/step-5.png',
  release: '/shopify-custom-app/step-6.png',
  distribute: '/shopify-custom-app/step-7.png',
  credentials: '/shopify-custom-app/step-8.png',
};

/** Shopify's Client ID and secret are long; this only catches a partial paste. */
const MIN_CREDENTIAL_LENGTH = 8;

export type InstallLinkCheck =
  | { status: 'empty' }
  | { status: 'ok' }
  | { status: 'invalid' }
  | { status: 'other_store'; host: string };

const isShopifyHost = (host: string): boolean =>
  host === 'shopify.com' || host.endsWith('.shopify.com') || host.endsWith('.myshopify.com');

export function checkInstallLink(raw: string, shopDomain: string): InstallLinkCheck {
  const value = raw.trim();
  if (!value) return { status: 'empty' };
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return { status: 'invalid' };
  }
  const host = url.hostname.toLowerCase();
  if (url.protocol !== 'https:' || !isShopifyHost(host)) return { status: 'invalid' };
  if (host.endsWith('.myshopify.com') && host !== shopDomain.toLowerCase()) {
    return { status: 'other_store', host };
  }
  const signed =
    /\/oauth\/install_custom_app\/?$/.test(url.pathname) &&
    Boolean(url.searchParams.get('signature')) &&
    Boolean(url.searchParams.get('client_id'));
  return signed ? { status: 'ok' } : { status: 'invalid' };
}

/** True when the install link was generated for a different app than this Client ID. */
export function installLinkForOtherApp(installLink: string, clientId: string): boolean {
  const id = clientId.trim();
  if (!id) return false;
  try {
    const linked = new URL(installLink.trim()).searchParams.get('client_id');
    return Boolean(linked) && linked !== id;
  } catch {
    return false;
  }
}

export interface CustomAppValues {
  clientId: string;
  clientSecret: string;
  installLink: string;
}

export function credentialsEntered(values: CustomAppValues): boolean {
  return (
    values.clientId.trim().length >= MIN_CREDENTIAL_LENGTH &&
    values.clientSecret.trim().length >= MIN_CREDENTIAL_LENGTH &&
    !installLinkForOtherApp(values.installLink, values.clientId)
  );
}

/** Whether the primary button is open on this step. Only steps 7 and 8 have a gate. */
export function stepGateOpen(step: CustomAppStepId, values: CustomAppValues, shopDomain: string): boolean {
  const linkOk = checkInstallLink(values.installLink, shopDomain).status === 'ok';
  if (step === 'distribute') return linkOk;
  if (step === 'credentials') return linkOk && credentialsEntered(values);
  return true;
}

/** A stored step id the wizard no longer has reads as the first step. */
export function resolveStoredStep(step: string | undefined): CustomAppStepId {
  return CUSTOM_APP_STEP_IDS.find((id) => id === step) ?? CUSTOM_APP_STEP_IDS[0];
}

export function resolveStoredDone(done: string[] | undefined): CustomAppStepId[] {
  return CUSTOM_APP_STEP_IDS.filter((id) => done?.includes(id));
}
